import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What\'s a "monthly dividend calendar"?',
    a: 'A retirement-income workflow where you combine holdings paying on different schedules so a dividend arrives every week or two of the month, smoothing your cash flow. Most US stocks pay quarterly on a regular cycle (Jan/Apr/Jul/Oct, Feb/May/Aug/Nov, or Mar/Jun/Sep/Dec). Mix one ticker from each cycle + a monthly payer or two and you have 52 dividend events a year instead of 4.',
  },
  {
    q: 'Why does this matter if the total income is the same?',
    a: 'Behaviour. A retiree receiving one $3K dividend in January and nothing until April either spends it down too fast or feels guilty saving it — both are common. Receiving $250/week feels like a paycheck and gets budgeted naturally. The math is identical; the lived experience is dramatically different.',
  },
  {
    q: 'What stocks pay monthly dividends?',
    a: 'Realty Income (O), Main Street Capital (MAIN), STAG Industrial (STAG), AGNC, Pembina Pipeline (PBA), LTC Properties (LTC), EPR Properties (EPR), and most covered-call ETFs (JEPI, JEPQ, QYLD, XYLD, DIVO, SPYI). Roughly 80 stocks and 60+ ETFs pay monthly. DiviDrip\'s screener has a Monthly filter that surfaces them all.',
  },
  {
    q: 'Should I switch entirely to monthly payers?',
    a: 'No. The quality of the underlying business matters more than the payment frequency. A great quarterly Dividend King beats a mediocre monthly REIT every time. Use monthly payers to fill calendar gaps, not as the entire portfolio.',
  },
  {
    q: 'How do I know which quarterly cycle a stock pays on?',
    a: 'Look at the last payment date. JNJ pays March/June/Sept/Dec. KO pays April/July/Oct/Jan. PG pays Feb/May/Aug/Nov. Once you know the cycle, you can stack one ticker from each cycle to cover every month. DiviDrip\'s Calendar tab shows your projected income month-by-month so the gaps are obvious.',
  },
];

export default function LearnMonthlyDividendCalendar() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The Monthly Dividend Calendar Trick — Smoothing Income for Retirees | DiviDrip"
      description="How to combine quarterly + monthly dividend payers so a dividend hits your account every week. The retirement-income smoothing trick that turns lumpy distributions into a paycheck."
      canonical="https://twylightcrow.com/learn/monthly-dividend-calendar"
      guidePath="/learn/monthly-dividend-calendar"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Retirement income"
      h1="The Monthly Dividend Calendar Trick"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/build-5k-monthly-income', label: 'Build a $5K monthly dividend income portfolio' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor\'s life' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles' },
        { to: '/learn/covered-call-etfs', label: 'Covered Call ETFs — JEPI, JEPQ, QYLD' },
      ]}
    >
      <p>
        Most US dividend stocks pay quarterly. That means a retiree relying on dividends
        for income gets four big payments per year and twelve months of variable cash
        flow. It's not life-or-death — but it's suboptimal compared to receiving
        income roughly weekly, like the paycheck you got pre-retirement.
      </p>
      <p>
        Here's the construction trick that solves it.
      </p>

      <h2>The three quarterly cycles</h2>
      <p>
        Quarterly-paying US stocks fall into one of three cycles:
      </p>
      <ul>
        <li><strong>Cycle 1</strong> — Jan / Apr / Jul / Oct (examples: KO, PEP, T)</li>
        <li><strong>Cycle 2</strong> — Feb / May / Aug / Nov (examples: PG, GIS, ABBV)</li>
        <li><strong>Cycle 3</strong> — Mar / Jun / Sep / Dec (examples: JNJ, MSFT, XOM, JPM)</li>
      </ul>
      <p>
        If you hold one ticker from each cycle, you have a dividend arriving every
        single month — already a huge improvement over quarterly lumpiness.
      </p>

      <h2>Adding monthly payers for true weekly cadence</h2>
      <p>
        Layer in 2-3 monthly-paying holdings and you go from monthly to roughly
        weekly. Monthly payers split into two camps:
      </p>
      <ul>
        <li><strong>Quality monthly REITs and BDCs:</strong> Realty Income (O), Main Street Capital (MAIN), STAG Industrial (STAG). These pay around the 12th-15th of each month.</li>
        <li><strong>Covered-call ETFs:</strong> JEPI, JEPQ, DIVO, SPYI, QYLD, XYLD. These pay near the start of the month.</li>
      </ul>
      <p>
        With one from each group plus three quarterly picks, you're receiving
        something nearly every week of the year.
      </p>

      <h2>A worked example — the "Smoothed Paycheck" portfolio</h2>
      <p>
        $500K hypothetical retirement portfolio. The goal: ~$2,000/month spread evenly.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Ticker</th>
            <th className="text-left font-semibold">Allocation</th>
            <th className="text-left font-semibold">Frequency</th>
            <th className="text-left font-semibold">Payment month(s)</th>
            <th className="text-left font-semibold">Est. annual income</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="font-semibold">SCHD</td><td>$100K</td><td>Quarterly</td><td className="font-mono text-xs">Mar/Jun/Sep/Dec</td><td className="font-mono">~$3,500</td></tr>
          <tr><td className="font-semibold">KO</td><td>$60K</td><td>Quarterly</td><td className="font-mono text-xs">Apr/Jul/Oct/Jan</td><td className="font-mono">~$1,900</td></tr>
          <tr><td className="font-semibold">PG</td><td>$60K</td><td>Quarterly</td><td className="font-mono text-xs">Feb/May/Aug/Nov</td><td className="font-mono">~$1,500</td></tr>
          <tr><td className="font-semibold">JNJ</td><td>$60K</td><td>Quarterly</td><td className="font-mono text-xs">Mar/Jun/Sep/Dec</td><td className="font-mono">~$1,800</td></tr>
          <tr><td className="font-semibold">O</td><td>$80K</td><td>Monthly</td><td className="font-mono text-xs">Mid-month</td><td className="font-mono">~$4,500</td></tr>
          <tr><td className="font-semibold">MAIN</td><td>$60K</td><td>Monthly</td><td className="font-mono text-xs">Mid-month</td><td className="font-mono">~$4,000</td></tr>
          <tr><td className="font-semibold">JEPI</td><td>$80K</td><td>Monthly</td><td className="font-mono text-xs">Start-of-month</td><td className="font-mono">~$6,400</td></tr>
        </tbody>
      </table>
      <p>
        <strong>Total ~$23,600/year ≈ $1,970/month average</strong> spread across
        roughly 50 distinct payment events.
      </p>

      <h2>Don't sacrifice quality for the calendar</h2>
      <p>
        The biggest mistake new retirees make is over-weighting monthly payers because
        of the "every month" appeal. Many monthly payers are higher-yield, lower-growth
        REITs / BDCs / leveraged CEFs — fine in moderation, dangerous as the bulk of
        the portfolio. The 4 quarterly Dividend King / ETF positions above carry the
        portfolio's long-term growth; the monthly payers smooth the calendar.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Build your calendar in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and head to the{' '}
        <strong>Calendar</strong> tab. Every projected dividend across your portfolio
        is plotted month-by-month so you can see exactly where the gaps are. Add one
        ticker at a time from the Screener's Monthly or Frequency filter and watch
        the calendar fill in until every week is covered.
      </p>
    </LearnPage>
  );
}
